import type { PlaybackMode } from "@recital-master/shared";

type WatchRouteParam = string | string[] | undefined;

export function firstWatchRouteParam(value: WatchRouteParam): string | undefined {
  if (typeof value === "string") return value || undefined;
  return value?.find((entry) => entry.length > 0);
}

export function watchEntryDanceId(
  dance: WatchRouteParam,
  legacyStart: WatchRouteParam
): string | undefined {
  return firstWatchRouteParam(dance) ?? firstWatchRouteParam(legacyStart);
}

/**
 * Mirror an in-player dance change into the browser address bar without
 * dispatching an Expo Router navigation. Safari attaches AirPlay to the
 * current HTMLVideoElement; a router remount would destroy that element and
 * release the receiver route.
 */
export function watchBrowserUrlForCurrentDance(
  href: string,
  danceId: string,
  preserveEntryPosition: boolean
): string {
  const url = new URL(href);
  url.searchParams.set("dance", danceId);
  url.searchParams.delete("start");
  if (!preserveEntryPosition) url.searchParams.delete("at");
  return `${url.pathname}${url.search}${url.hash}`;
}

export function watchPathForDance(
  performanceId: string,
  mode: PlaybackMode,
  danceId: string,
  dancerId?: string,
  options: { reattach?: boolean; scope?: "recital" } = {}
): string {
  const query = [
    ["mode", mode],
    ["scope", options.scope],
    ["dance", danceId],
    ["dancer", dancerId],
    ["reattach", options.reattach ? "1" : undefined]
  ]
    .filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === "string" && entry[1].length > 0
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return `/watch/${encodeURIComponent(performanceId)}?${query}`;
}

export function recitalMyDancerWatchPath(
  recitalId: string,
  options: {
    dancerId?: string;
    danceId?: string;
    positionSeconds?: number;
  } = {}
): string {
  const query = [
    ["mode", "my-dancer"],
    ["scope", "recital"],
    ["dance", options.danceId],
    ["dancer", options.dancerId],
    [
      "at",
      typeof options.positionSeconds === "number"
        ? String(Math.max(0, Math.floor(options.positionSeconds)))
        : undefined
    ]
  ]
    .filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === "string" && entry[1].length > 0
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return `/watch/${encodeURIComponent(recitalId)}?${query}`;
}
